import { buildPersonIdentity } from '../domain/model/PersonIdentity';

/**
 * Reconstructs an identity that arrived over the wire.
 *
 * THE BACKEND ALREADY RESOLVED THIS. `getClientUsers` returns an identity per row, so a caller that
 * picks a row is not looking anything up: it is taking an answer the server already gave and making
 * it hold together on this side. Reconstructing rather than trusting matters because a client can be
 * one release behind a server, or reading a cached row written by a version that predates the field.
 *
 * NULL FOR ANYTHING THAT CANNOT NAME A PERSON, rather than a throw or a half-built value. A row
 * whose account has nobody behind it is an ordinary state on a screen — a login that was never
 * linked to a staff profile — and a dropdown must still be able to render it. The caller decides
 * whether that row may be chosen; refusing to build is what makes the decision unavoidable.
 *
 * @param {{personId?: string|null, userId?: string|null, userName?: string|null}|null|undefined} dto
 * @returns {import('../domain/model/PersonIdentity').PersonIdentity|null}
 */
export const toPersonIdentity = (dto) => {
  if (typeof dto?.personId !== 'string' || dto.personId.length === 0) return null;

  return buildPersonIdentity({
    personId: dto.personId,
    userId: dto.userId,
    userName: dto.userName,
  });
};

/**
 * The person an account belongs to, from the account record itself.
 *
 * THE OTHER ENTRY POINT, and the reason it is a named operation rather than a second call to the
 * reconstruction above. An account record is not a wire DTO: it carries `staffMember`, the
 * platform's historical name for the person a login belongs to. Translating it here means one file
 * knows that mislabel, instead of every caller that has an account record in its hands.
 *
 * @param {{userId?: string|null, staffMember?: string|null, name?: string|null}|null|undefined} account
 * @returns {import('../domain/model/PersonIdentity').PersonIdentity|null}
 */
export const toAccountPersonIdentity = (account) => toPersonIdentity({
  personId: account?.staffMember,
  userId: account?.userId,
  userName: account?.name,
});

/**
 * The same resolution, in the shape a server would have sent it.
 *
 * The offline read answers in the Cloud Function's place, and a callable's response is JSON. A
 * frozen value here would make `identity` arrive in two shapes under one name.
 *
 * @param {{userId?: string|null, staffMember?: string|null, name?: string|null}|null|undefined} account
 * @returns {{personId: string, userId: string|null, userName: string|null}|null}
 */
export const toAccountIdentityDto = (account) => {
  const identity = toAccountPersonIdentity(account);

  return identity === null ? null : { ...identity };
};
